import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Card, CardSection } from '@trafiflow/shared-ui'
import { agentplatService } from '../../services/agentplat'
import type { Agent } from '../../services/agentplat/types'
import { User, MessageSquare, Users, ShoppingCart, Loader2 } from 'lucide-react'
import { ROUTES } from '../config/routes'

/**
 * Home Page - Dashboard
 * 
 * Main dashboard showing agent statistics and quick access to chat with contracted agents
 */
export default function Home() {
  const navigate = useNavigate()
  const [contractedAgents, setContractedAgents] = useState<Agent[]>([])
  const [availableAgents, setAvailableAgents] = useState<Agent[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true)
      setError(null)

      try {
        // Fetch contracted agents (my agents) - handle independently
        try {
          const contracted = await agentplatService.listAgents()
          setContractedAgents(contracted)
        } catch {
          setContractedAgents([])
        }

        // Fetch available agents from marketplace - handle independently
        try {
          const available = await agentplatService.listMarketAgents()
          setAvailableAgents(available)
        } catch {
          setAvailableAgents([])
        }
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [])

  const handleChatClick = (agentId: string) => {
    navigate(`/agents/${agentId}?tab=chat`)
  }

  const handleViewAgent = (agentId: string) => {
    navigate(`/agents/${agentId}`)
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
          <p className="text-tf-text-muted">Loading dashboard...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-8 text-center bg-tf-feedback-error-bg rounded-lg border border-tf-feedback-error-border">
        <p className="text-tf-feedback-error-text font-medium">{error}</p>
        <button
          onClick={() => window.location.reload()}
          className="mt-4 px-4 py-2 bg-tf-feedback-error-text text-white rounded-md hover:opacity-90 transition-colors"
        >
          Retry
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-tf-text">Home</h1>
        <p className="text-tf-text-muted mt-2">Welcome to your dashboard</p>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardSection>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-tf-text-muted">Contracted Agents</p>
                <p className="text-3xl font-bold text-tf-text mt-2">{contractedAgents.length}</p>
              </div>
              <div className="w-12 h-12 bg-tf-feedback-info-bg rounded-lg flex items-center justify-center">
                <Users className="w-6 h-6 text-tf-feedback-info-text" />
              </div>
            </div>
          </CardSection>
        </Card>

        <Card>
          <CardSection>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-tf-text-muted">Available Agents</p>
                <p className="text-3xl font-bold text-tf-text mt-2">{availableAgents.length}</p>
              </div>
              <div className="w-12 h-12 bg-tf-feedback-success-bg rounded-lg flex items-center justify-center">
                <ShoppingCart className="w-6 h-6 text-tf-feedback-success-text" />
              </div>
            </div>
          </CardSection>
        </Card>
      </div>

      {/* Contracted Agents Cards */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-tf-text">Contracted Agents</h2>
          {contractedAgents.length > 0 && (
            <button
              onClick={() => navigate(ROUTES.AGENTS.MY_AGENTS)}
              className="text-sm text-primary hover:underline"
            >
              View all
            </button>
          )}
        </div>

        {contractedAgents.length === 0 ? (
          <Card>
            <CardSection className="text-center py-12">
              <div className="flex flex-col items-center gap-4">
                <div className="w-16 h-16 bg-tf-bg-hover rounded-full flex items-center justify-center">
                  <Users className="w-8 h-8 text-tf-text-muted" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-tf-text">No contracted agents</h3>
                  <p className="text-tf-text-muted max-w-sm mx-auto mt-2">
                    You don't have any contracted agents yet. Visit the Agent Market to find and contract agents.
                  </p>
                </div>
                <button
                  onClick={() => navigate(ROUTES.AGENTS.MARKET)}
                  className="mt-4 px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90 transition-colors"
                >
                  Browse Agent Market
                </button>
              </div>
            </CardSection>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {contractedAgents.map((agent) => (
              <Card 
                key={agent.id} 
                className="hover:shadow-lg transition-all duration-200 cursor-pointer"
                onClick={() => handleViewAgent(agent.id)}
              >
                <CardSection>
                  <div className="flex items-start gap-4 mb-4">
                    {agent.avatar ? (
                      <img
                        src={agent.avatar}
                        alt={agent.name}
                        className="w-16 h-16 rounded-lg object-cover flex-shrink-0 border border-tf-border"
                      />
                    ) : (
                      <div className="w-16 h-16 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0 border border-tf-border">
                        <User className="w-8 h-8 text-primary" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-tf-text truncate">{agent.name}</h3>
                      {agent.role && (
                        <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-tf-feedback-info-bg text-tf-feedback-info-text mt-1">
                          {typeof agent.role === 'string' ? agent.role : agent.role.name}
                        </span>
                      )}
                    </div>
                  </div>
                  
                  {agent.description && (
                    <p className="text-sm text-tf-text-muted line-clamp-2 mb-4">
                      {agent.description}
                    </p>
                  )}

                  <div className="flex items-center justify-between pt-4 border-t border-tf-border">
                    <div className="flex items-center gap-2">
                      <span
                        className={`w-2 h-2 rounded-full ${ agent.status === 'online' || agent.status === 'active' ? 'bg-tf-success' : 'bg-tf-text-muted' }`}
                      ></span>
                      <span className="text-xs text-tf-text-muted capitalize">
                        {agent.status || 'offline'}
                      </span>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        handleChatClick(agent.id)
                      }}
                      className="flex items-center gap-2 px-3 py-1.5 bg-primary text-white text-sm rounded-md hover:bg-primary/90 transition-colors"
                    >
                      <MessageSquare className="w-4 h-4" />
                      Chat
                    </button>
                  </div>
                </CardSection>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}


